// tokenRefreshService.js
import { serviceHTTP } from './service';
import { store } from '../store/configureStore';
import { getRefreshToken, clearSessionData, setNewTokens } from './customMethods';

class TokenRefreshService {
  constructor() {
    this.isRefreshing = false;
    this.failedQueue = [];
  }

  // Process the queue of failed requests
  processQueue(error, token = null) {
    this.failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    
    this.failedQueue = [];
  }

  // Add request to queue
  addToQueue(resolve, reject) {
    this.failedQueue.push({ resolve, reject });
  }

  // Refresh the token
  async refreshToken() {
    const refreshToken = getRefreshToken();
    
    if (!refreshToken) {
      // No refresh token available, clear session and redirect
      store.dispatch(clearSessionData());
      throw new Error('No refresh token available');
    }

    // A network error or a non-JSON (e.g. 502) reply throws here without ending the session.
    const response = await serviceHTTP.getAccessTokenWithRefreshToken(refreshToken);

    if (response.status === 200 && response.access_token) {
      store.dispatch(setNewTokens(response.access_token, response.refresh_token));
      return response.access_token;
    }

    if ([400, 401].includes(response.status)) {
      // The server rejected the refresh token: the session is really over
      store.dispatch(clearSessionData());
      throw new Error('Token refresh failed');
    }

    throw new Error('Invalid response from token refresh');
  }

  // Main method to handle 401 errors
  async handleUnauthorized(originalRequest) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;

      try {
        const newToken = await this.refreshToken();
        this.isRefreshing = false;
        this.processQueue(null, newToken);
        
        // Retry original request with new token
        return this.retryRequest(originalRequest, newToken);
      } catch (error) {
        this.isRefreshing = false;
        this.processQueue(error, null);
        throw error;
      }
    } else {
      // If token is being refreshed, queue the request
      return new Promise((resolve, reject) => {
        this.addToQueue(
          (token) => resolve(this.retryRequest(originalRequest, token)),
          (error) => reject(error)
        );
      });
    }
  }

  // Retry the original request with new token
  async retryRequest(originalRequest, newToken) {
    const { method, url, data, headers = {}, isDispatcher } = originalRequest;
    
    // Update headers with new token
    const newHeaders = {
      ...headers,
      Authorization: `Bearer ${newToken}`
    };

    // For PUT and DELETE, we need to handle token in URL
    if (method.toLowerCase() === 'put' || method.toLowerCase() === 'delete') {
      // Remove old token from URL if present
      let cleanUrl = url;
      if (url.includes('access_token=')) {
        cleanUrl = url.split('access_token=')[0];
        if (cleanUrl.endsWith('&') || cleanUrl.endsWith('?')) {
          cleanUrl = cleanUrl.slice(0, -1);
        }
      }
      // Add new token as query parameter for PUT/DELETE
      const separator = cleanUrl.includes('?') ? '&' : '?';
      const urlWithToken = `${cleanUrl}${separator}access_token=${newToken}`;
      
      switch (method.toLowerCase()) {
        case 'put':
          return serviceHTTP.put(urlWithToken, data, headers);
        case 'delete':
          return serviceHTTP.delete(urlWithToken, headers);
      }
    }

    // For GET and POST, use header authentication
    switch (method.toLowerCase()) {
      case 'get':
        return serviceHTTP.get(url, newHeaders, newToken);
      case 'post':
        return isDispatcher ? 
          serviceHTTP.postToDispatch(url, data, newHeaders, newToken) :
          serviceHTTP.post(url, data, newHeaders, newToken);
      default:
        throw new Error(`Unsupported method: ${method}`);
    }
  }
}

export const tokenRefreshService = new TokenRefreshService();